import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_STYLES = {
  normal: {
    dotClass: 'bg-emerald-400',
    chipClass: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/40',
    label: 'Normal',
  },
  attention: {
    dotClass: 'bg-amber-400',
    chipClass: 'bg-amber-500/20 text-amber-200 border-amber-400/50',
    label: 'Attention',
  },
  review: {
    dotClass: 'bg-purple-400',
    chipClass: 'bg-purple-500/25 text-purple-200 border-purple-400/50',
    label: 'Review',
  },
};

const formatDeloadAgo = (lastDeloadAt) => {
  if (!lastDeloadAt) return 'No deload recorded';
  try {
    const last = new Date(lastDeloadAt + 'T00:00:00Z');
    const now = new Date();
    const days = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    if (days < 7) return `Last Deload: ${days}d ago`;
    const weeks = Math.round(days / 7);
    return `Last Deload: ${weeks} weeks ago`;
  } catch {
    return 'No deload recorded';
  }
};

/**
 * Operational coaching signal — trainer/admin only.
 * Variant 'chip': full chip with label + extras (header in ClientDetailPage).
 * Variant 'dot':  tiny corner dot + tooltip (BookingPlanner cards).
 * Both are PURE READS — no DB writes, no client-facing surfaces.
 */
export const ContinuityChip = ({ data, variant = 'chip', className = '' }) => {
  if (!data) return null;
  const tier = data.tier || 'normal';
  const style = TIER_STYLES[tier] || TIER_STYLES.normal;
  const weeks = data.block_weeks ?? 0;
  const freq = data.expected_freq ?? 2;

  if (variant === 'dot') {
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${style.dotClass} ${className}`}
        title={`Continuity block: ${weeks}w · ${style.label} · ${freq}×/wk · ${formatDeloadAgo(data.last_deload_at)}`}
        data-testid={`continuity-dot-${tier}`}
      />
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs ${style.chipClass} ${className}`}
      data-testid={`continuity-chip-${tier}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dotClass}`} />
      <span className="font-semibold">{weeks} accumulated weeks</span>
      <span className="opacity-80">· {style.label}</span>
      <span className="opacity-60 hidden md:inline">
        · {freq}×/wk · {data.active_weeks_in_last_12 ?? 0}/12 active
      </span>
      {data.last_deload_at && (
        <span className="opacity-60 hidden lg:inline">· {formatDeloadAgo(data.last_deload_at)}</span>
      )}
    </div>
  );
};

/**
 * Hook: fetch continuity for one client.
 * Returns { data, loading, error, refresh }.
 */
export const useContinuity = (clientId) => {
  const [state, setState] = useState({ data: null, loading: !!clientId, error: null });

  const refresh = React.useCallback(async () => {
    if (!clientId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/trainer/continuity/${clientId}`);
      setState({ data: r.data, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e });
    }
  }, [clientId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
};

/**
 * Hook: bulk fetch continuity for many clients in one round-trip.
 * Returns { byClient: {client_id: data}, loading, error, refresh }.
 */
export const useContinuityBulk = (clientIds) => {
  const [state, setState] = useState({ byClient: {}, loading: false, error: null });
  const key = (clientIds || []).slice().sort().join(',');

  useEffect(() => {
    if (!key) {
      setState({ byClient: {}, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    axios
      .get(`${API}/trainer/continuity`, { params: { client_ids: key } })
      .then((r) => {
        if (!cancelled) setState({ byClient: r.data || {}, loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setState({ byClient: {}, loading: false, error: e });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
};

export default ContinuityChip;
